import { get } from '@rover/react-lib/src/apis/experiments.api';
import fetchExperimentsResponseFactory from '@rover/react-lib/src/factories/fetchExperimentsResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { Experiment, PaginatedFrontendExperimentList } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

import generateActions from './generateActions';

const CONTROL_GROUP = 'control';
const VARIANT_GROUP = 'variant';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchExperimentsResponse: PaginatedFrontendExperimentList;
};

type Actions = {
  fetchExperimentsPending: ActionCreator<void>;
  fetchExperimentsSuccess: ActionCreator<PaginatedFrontendExperimentList>;
  fetchExperimentsFailure: ActionCreator<string>;
};

type Effects = {
  fetchExperiments: EffectCreator<void>;
};

type Selectors = {
  getExperiments: SelectorCreator<Experiment[]>;
  isInExperimentGroup: SelectorCreator<(arg0: string, arg1: string) => boolean>;
  isControl: SelectorCreator<(arg0: string) => boolean>;
  isVariant: SelectorCreator<(arg0: string) => boolean>;
  isCustomVariantGroup: SelectorCreator<(arg0: string, arg1: string) => boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'experiments',
  initialState: {
    errorMessage: '',
    loading: true,
    fetchExperimentsResponse: fetchExperimentsResponseFactory(),
  },
  actions: { ...generateActions('fetchExperiments') },
  effects: {
    fetchExperiments:
      () =>
      async (
        dispatch,
        getState,
        { fetchExperimentsPending, fetchExperimentsSuccess, fetchExperimentsFailure }
      ) => {
        dispatch(fetchExperimentsPending());

        try {
          const fetchExperimentsResponse: PaginatedFrontendExperimentList = await get();
          dispatch(fetchExperimentsSuccess(fetchExperimentsResponse));
        } catch (e) {
          dispatch(fetchExperimentsFailure((e as Error).message));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getExperiments: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchExperimentsResponse.results),
    isInExperimentGroup: (getState, createSelector, { getExperiments }) =>
      createSelector(
        [getExperiments],
        (experiments) => (experimentName, group) =>
          !!experimentName &&
          !!experimentName.length &&
          !!group &&
          !!group.length &&
          !!experiments.find((e) => e.name === experimentName && e.group === group)
      ),
    isControl: (getState, createSelector, selectors) =>
      createSelector(
        [selectors.isInExperimentGroup],
        (isInExperimentGroup) => (experimentName) =>
          isInExperimentGroup(experimentName, CONTROL_GROUP)
      ),
    isVariant: (getState, createSelector, selectors) =>
      createSelector(
        [selectors.isInExperimentGroup],
        (isInExperimentGroup) => (experimentName) =>
          isInExperimentGroup(experimentName, VARIANT_GROUP)
      ),
    isCustomVariantGroup: (getState, createSelector, selectors) =>
      createSelector(
        [selectors.isInExperimentGroup],
        (isInExperimentGroup) => (experimentName, group) =>
          isInExperimentGroup(experimentName, group)
      ),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
